import * as THREE from 'three';

const energyMaterial = (color, opacity = 1) =>
  new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false,
    toneMapped: false,
    blending: THREE.AdditiveBlending,
  });

// A contact burst and a short smoke trail follow the defending die. They are
// presentation only: the combat transaction and damage splat own hit timing.
export const createCoreHitImpact = (parent) => {
  const group = new THREE.Group();
  group.name = 'core-die-impact';
  group.visible = false;
  parent.add(group);
  const flash = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.7, 2),
    energyMaterial(0xffffff)
  );
  const halo = new THREE.Mesh(
    new THREE.SphereGeometry(1, 16, 12),
    energyMaterial(0x70eaff, 0.3)
  );
  group.add(flash, halo);
  const rings = Array.from({ length: 3 }, () => {
    const mesh = new THREE.Mesh(
      new THREE.TorusGeometry(1, 0.045, 6, 48),
      energyMaterial(0x70eaff)
    );
    group.add(mesh);
    return mesh;
  });
  const streaks = Array.from({ length: 16 }, () => {
    const mesh = new THREE.Mesh(
      new THREE.ConeGeometry(0.065, 1, 4),
      energyMaterial(0xffffff)
    );
    group.add(mesh);
    return mesh;
  });
  const smokeGeometry = new THREE.IcosahedronGeometry(0.25, 1);
  const smoke = Array.from({ length: 12 }, () => {
    const mesh = new THREE.Mesh(
      smokeGeometry,
      new THREE.MeshBasicMaterial({
        color: 0x8299a6,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      })
    );
    group.add(mesh);
    return mesh;
  });
  const right = new THREE.Vector3();
  const up = new THREE.Vector3();
  const direction = new THREE.Vector3();
  const axis = new THREE.Vector3(0, 1, 0);
  const update = ({
    active,
    elapsed,
    position,
    camera,
    appearance,
    reduceMotion,
  }) => {
    group.visible =
      active && elapsed >= 0 && elapsed < (reduceMotion ? 0.65 : 2.8);
    if (!group.visible) return;
    group.position.copy(position);
    const burst = Math.max(0, 1 - elapsed / 0.55);
    flash.visible = halo.visible = burst > 0;
    flash.material.color.setHex(appearance.hot);
    flash.material.opacity = burst * (reduceMotion ? 0.35 : 0.95);
    flash.scale.setScalar(reduceMotion ? 0.8 : 0.65 + (1 - burst) * 1.5);
    halo.material.color.setHex(appearance.attack);
    halo.material.opacity = burst * 0.3;
    halo.scale.setScalar(reduceMotion ? 1.1 : 1 + (1 - burst) * 1.4);
    right.set(1, 0, 0).applyQuaternion(camera.quaternion);
    up.set(0, 1, 0).applyQuaternion(camera.quaternion);
    rings.forEach((ring, index) => {
      const age = elapsed - index * 0.07;
      ring.visible = age >= 0 && age < 0.7 && (!reduceMotion || index === 0);
      if (!ring.visible) return;
      ring.quaternion.copy(camera.quaternion);
      ring.scale.setScalar(
        reduceMotion ? 1.1 : 0.45 + age * (3.2 + index * 0.6)
      );
      ring.material.color.setHex(
        index === 1 ? appearance.hot : appearance.accent
      );
      ring.material.opacity =
        Math.max(0, 1 - age / 0.7) * (reduceMotion ? 0.3 : 0.8);
    });
    streaks.forEach((streak, index) => {
      streak.visible = burst > 0 && !reduceMotion;
      if (!streak.visible) return;
      const angle = index * 2.399;
      direction
        .copy(right)
        .multiplyScalar(Math.cos(angle))
        .addScaledVector(up, Math.sin(angle));
      streak.position
        .copy(direction)
        .multiplyScalar(0.7 + elapsed * (3 + (index % 4)));
      streak.quaternion.setFromUnitVectors(axis, direction);
      streak.scale.set(1, 0.5 + burst * 0.8, 1);
      streak.material.color.setHex(
        index % 3 ? appearance.accent : appearance.hot
      );
      streak.material.opacity = burst * 0.85;
    });
    smoke.forEach((puff, index) => {
      const age = elapsed - 0.15 - index * 0.045;
      puff.visible = !reduceMotion && age > 0 && age < 2.1;
      if (!puff.visible) return;
      const angle = index * 2.399;
      puff.position.set(
        Math.cos(angle) * (0.18 + age * 0.16),
        0.3 + age * (0.7 + (index % 3) * 0.15),
        Math.sin(angle) * 0.25
      );
      puff.scale.setScalar(0.65 + age * 0.9);
      puff.rotation.set(age * 0.3, angle + age * 0.2, 0);
      puff.material.opacity =
        Math.min(1, age * 6) * Math.max(0, 1 - age / 2.1) * 0.26;
    });
  };
  return { group, flash, rings, streaks, smoke, update };
};
